import {App, Button, Image, Modal, Space} from "antd";
import React, { useRef, useState, useEffect } from "react";
import styles from "./index.module.scss";
import { useQuery, useMutation } from "react-query";
import jwtAxios from "../../../../auth/jwt-api";
import { API_URL } from "../../../../shared/constants";
import Loading from "../../../../layouts/components/Loading/index";
import { clsx } from "clsx";
import OrderModel from "./OrderModel";
import PaymentModel from "./PaymentModel";
import { uz } from "date-fns/locale";
import { format } from "date-fns";
import { MdDelete } from "react-icons/md";
import { DownloadOutlined, ExclamationCircleFilled, EyeOutlined } from "@ant-design/icons";
import { useAuthDatas } from "../../../../auth/JWTAuthProvider";
import { useReactToPrint } from "react-to-print";
import Print from "./Print";
import { formatNumberWith } from "../../../../shared/utils/formatNumberWithDots";
import translate from "../../../../i18n/translate";
import OrderParkingModel from "./OrderParkingModel";
import {formatPriceParking} from "../../../../utils/formatPriceParking";
import {useSelector, useDispatch} from "react-redux";
import {attachUser, clearAttach} from "../../../../slice/userData";
import OrderLogModal from "./OrderLogModal";
import OfflineContractUpload from "./OfflineContractUpload";

const { confirm } = Modal;

export default function ParkingModel({
  searchParams,
  setSearchParams,
  refetchParking,
}) {
  const { message } = App.useApp();

  const { user } = useAuthDatas();
  const dispatch = useDispatch();
  const {userData} = useSelector(state => state.userData);
  const [orderLogOpen, setOrderLogOpen] = useState(false);
  const [contractLoading, setContractLoading] = useState(false);
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [photo_from_camera, setPhoto_from_camera] = useState("");
  const [myIdLoading, setMyIdLoading] = useState(false);
  const [isVerifyMode, setIsVerifyMode] = useState(false);
  const {rate} = useSelector(state => state.rate)

  const {
    data: parkingRes,
    isLoading,
    refetch: refetchInfo,
  } = useQuery(
    ["parking", searchParams.parkingId],
    () => jwtAxios.get(`/parking/${searchParams.parkingId}`),
    {
      enabled: !!searchParams.parkingId,
    }
  );
  const { mutate: sendSms, isLoading: smsLoading } = useMutation(
    (data) => jwtAxios.post('/sms', data),
    {
      onSuccess: () => {
        message.success("SMS muvaffaqiyatli yuborildi");
      },
      onError: (error) => {
        message.error(error.response?.data?.error || "SMS yuborishda xatolik yuz berdi");
      }
    }
  );

  const parking = parkingRes?.data?.data?.parking;
  const order = parking?.orders?.[0];

  // MyID identify request when photo_from_camera is available
  useEffect(() => {
    if (photo_from_camera && userData?.pnfl && userData?.birthDate && searchParams.parkingId) {
      const identifyUser = async () => {
        setMyIdLoading(true);
        try {
          const response = await jwtAxios.post('/myId/identify', {
            pinfl: userData.pnfl,
            birth_date: userData.birthDate,
            front: photo_from_camera,
            contractId: order?.contracts?.[0]?.id,
            checkUser: isVerifyMode
          });
          if (response?.data?.data?.status_code===1){
            refetchInfo()
            message.success(isVerifyMode ? "Пользователь подтвердил" : "Договор подписан через MyID");
          }else  {
            message.warning(response?.data?.data?.status_text)
          }
        } catch (error) {
          const details = error?.response?.data?.error?.detail;
          if (Array.isArray(details)) {
            details.forEach(err => {
              const msg = err?.msg || "";
              if (msg.toLowerCase().includes("pinfl")) {
                message.error("PINFL — неверно.");
              } else if (msg.toLowerCase().includes("birth_date")) {
                message.error("Неверная дата рождения");
              } else if (msg.toLowerCase().includes("minimum required age is 14")) {
                message.error("Минимальный возраст — 14 лет.");
              } else {
                message.error(msg || "Произошла ошибка при проверке MyID.");
              }
              console.error("MyID identify error:", msg);
            });
          } else {
            message.error(error?.response?.data?.error || "Произошла ошибка при проверке MyID.");
            console.error("MyID identify error:", error);
          }
        } finally {
          setMyIdLoading(false);
        }
      };
      identifyUser();
    }
  }, [photo_from_camera, searchParams.parkingId]);

  // Save client to redux when order exists
  useEffect(() => {
    if (order?.client) {
      dispatch(attachUser({
        id: order.client.id,
        customerName: order.client.customerName || "",
        customerPhone: order.client.customerPhone || "",
        customerPhone2: order.client.customerPhone2 || "",
        email: order.client.email || "",
        address: order.client.address || "",
        clientType: order.client.clientType || "individual",
        passportGivenPlace: order.client.passportGivenPlace || "",
        passportGivenDate: order.client.passportGivenDate || "",
        series: order.client.series || "",
        pnfl: order.client.pnfl || "",
        birthDate: order.client.birthDate || "",
        director: order.client.director || "",
        bank: order.client.bank || "",
        bank_account: order.client.bank_account || "",
        mfo_code: order.client.mfo_code || "",
        oked_code: order.client.oked_code || "",
        inn: order.client.inn || "",
        favorites: order.client.favorites || []
      }));
    }
  }, [order?.client?.id]);

  const deletePayment = (payment) => {
    confirm({
      title: "Вы действительно хотите отменить платеж?",
      icon: <ExclamationCircleFilled />,
      content: "После удаления платежа восстановить его невозможно!",
      okText: "Удалить",
      cancelText: "Назад",
      async onOk() {
        try {
          await jwtAxios.delete(`/payment/${payment.id}`);
          message.success("To'lov o'chirildi");
          refetchInfo();
        } catch (error) {
          message.error(error.response?.data?.error || translate("payment.delete_error"));
        }
      },
    });
  };
  const deleteOrder = (order) => {
    confirm({
      title: "Вы уверены, что хотите отменить заказ?",
      icon: <ExclamationCircleFilled />,
      content: "После отмены заказа его невозможно восстановить, и все платежи будут потеряны!",
      okText: "v",
      cancelText: "Назад",
      async onOk() {
        try {
          await jwtAxios.delete(`/order/${order.id}`);
          message.success("Buyurtma o'chirildi");
          refetchInfo();
          refetchParking();
        } catch (error) {
          message.error(
            error.response?.data?.error || translate("order.delete_error")
          );
        }
      },
    });
  };

  const generateContract = async () => {
    if (!order?.id) return;
    setContractLoading(true);
    try {
      await jwtAxios.post(`/contract/generate`, { orderId: order.id });
      message.success("Shartnoma muvaffaqiyatli yaratildi");
      refetchInfo();
    } catch (error) {
      message.error(error.response?.data?.error || "Произошла ошибка.");
    } finally {
      setContractLoading(false);
    }
  };

  const OnCancelIframe = () => {
    const iframe = document.getElementById("myid_iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({cmd: 'close'}, '*');
    }
    setIframeModalOpen(false);
  };

  const OpenModalIframe = (verifyMode = false) => {
    setIsVerifyMode(verifyMode);
    setIframeModalOpen(true);
  };

  const handleIframeLoad = () => {
    const iframe = document.getElementById("myid_iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({cmd: 'open', config: {locale: 'uz', disableSwitch: true,threshold:250}}, '*');
    }
  };

  useEffect(() => {
    const handleMessage = (e) => {
      if (e.data?.cmd === 2 && e?.data?.photo_from_camera) {
        setPhoto_from_camera(e.data.photo_from_camera);
        const iframe = document.getElementById("myid_iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage({cmd: 'close'}, '*');
        }
        setIframeModalOpen(false);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  return (
    <>

      <Modal
        open={searchParams.parkingId}
        onOk={() => setSearchParams({ ...searchParams, orderModel: "true" })}
        onCancel={() => {
          setSearchParams({ ...searchParams, parkingId: undefined });
        }}
        width={1000}
        loading={myIdLoading}
        // title={parking?.catalog?.name}
        style={{ top: "50%", translate: "0 -50%" }}
        footer={
          <>
            <Button
              onClick={() => {
                setSearchParams({ ...searchParams, parkingId: undefined });
              }}
            >
              {translate("button.cancel")}
            </Button>
            {" "}
            {/* {user?.role !== "manager" && ( */}
            <Button
              type="primary"
              onClick={() =>
                setSearchParams({ ...searchParams, orderParkingModel: "true" })
              }
            >
              {order ? translate("button.edit") : translate("button.sell")}
            </Button>
            {/* )} */}
          </>
        }
      >
        <div style={{ height: "50vh",width:'100%' }}>
          {isLoading ? (
            <Loading
              style={{
                height: "100%",
              }}
            ></Loading>
          ) : (
            parking && (
              <div className={styles["model-content-parking"]}>
                <OrderParkingModel
                  setSearchParams={setSearchParams}
                  searchParams={searchParams}
                  refetchParking={refetchParking}
                  refetchInfo={refetchInfo}
                  order={order}
                  parking={parking}
                />

                <div className={styles.datasParking}>
                  <div className={clsx(styles.card)}>
                    <h2>{translate("parking.info")}</h2>
                    <Space>
                      <b>{translate("apartment.size")}</b>
                      <p>
                        {parseFloat(parking.m2)} {translate("misc.sqm")}
                      </p>
                    </Space>
                    <br/>
                    <Space>
                      <b>{translate("parking.number")}</b>
                      <p>
                        {parking.number}
                      </p>
                    </Space>
                   <br/>
                    <Space>
                      <b>{translate("apartment.floor")}</b>
                      <p>-1</p>
                    </Space>{" "}
                    <br />{" "}
                    <Space>
                      <b>{translate("apartment.status")}</b>
                      <p>
                        {{
                          sold: translate("status.sold"),
                          reserved: translate("status.booked"),
                          not_full_paid: translate("status.not_paid"),
                        }[order?.type] || translate("status.empty")}
                      </p>
                    </Space>
                    <br/>
                    {parking.storageRoomM2 && (
                      <>
                        <Space>
                          <b>{translate("menu.storage_room")}</b>
                          <p>
                            {parseFloat(parking.storageRoomM2)} {translate("misc.sqm")}
                          </p>
                        </Space>
                        <br/>
                      </>
                    )}

                    <br />
                  </div>
                  {order && (
                    <>
                      <PaymentModel
                        setSearchParams={setSearchParams}
                        searchParams={searchParams}
                        refetchInfo={refetchInfo}
                        order={order}
                      />
                      <div className={clsx(styles.card)}>
                        <h2>{translate("sale.info")}</h2>
                        <Space>
                          <b>{translate("sale.customer_name")}</b>
                          <p>{order?.client?.customerName}</p>
                        </Space>{" "}
                        <br />
                        <Space>
                          <b>{translate("sale.customer_phone")}</b>
                          <p>{order?.client?.customerPhone}</p>
                        </Space>

                        {order?.type !== "reserved" && (
                          <>
                            <br />
                            <Space>
                              <b>{translate("table.column.total_payment")}</b>
                              <p>
                                {order?.price?.toString()
                                    .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                              </p>
                            </Space>
                            <br/>
                            {order?.type === "reserved_paid" && (
                              <Space>
                                <b>{translate("sale.reserved.day")}</b>
                                <p>{1}</p>
                              </Space>
                            )}
                          </>
                        )}

                        <br /> <b>{translate("sale.additional_info")}</b>
                        <p>{order.description} </p>
                        <Space>
                          <b>{translate("sale.user")}</b>
                          <p>
                            {order?.user?.username}{" "}

                          </p>
                        </Space>
                        <br />
                        {order?.fileMedia?.url && (
                            <>
                              <Space>
                                <b>{translate("form.label.attached_file")}</b>
                                <Button
                                    type="primary"
                                    ghost
                                    icon={<DownloadOutlined />}
                                    href={`${process.env.REACT_APP_API_URL}/${order.fileMedia.url}`}
                                    target="_blank"
                                    download
                                >
                                  {translate("button.download")}
                                </Button>
                              </Space>
                              <br/>
                              {order?.file_comment && (
                                  <>
                                    <Space>
                                      <b>{translate("form.label.file_comment")}</b>
                                      <p>{order.file_comment}</p>
                                    </Space>
                                    <br/>
                                  </>
                              )}
                            </>
                        )}
                        <br />
                        {user?.role !== "manager" && (
                          <Button
                            danger
                            type="primary"
                            onClick={() => deleteOrder(order)}
                          >
                            {translate("sale.delete_order")}
                          </Button>
                        )}
                        <br />
                        {order?.type !== "reserved" && (
                        <Space size="middle" style={{marginTop: 10, flexWrap: 'wrap'}}>
                          {
                              (order?.client?.series || order?.client?.inn)
                              &&
                              <Button
                                  type="primary"
                                  disabled={order?.contracts?.[0]?.my_signature}
                                  onClick={generateContract}
                                  loading={contractLoading}
                              >
                                {translate("button.generate_contract")}
                              </Button>
                          }
                          {!order?.client?.series && !order?.client?.inn && (
                              <Button
                                  type="primary"
                                  onClick={() => OpenModalIframe(true)}
                              >
                                {translate("button.verify_user")}
                              </Button>
                          )}
                          {order?.contracts?.[0]?.contract_file && (
                            <>
                              <Button
                                type="primary"
                                ghost
                                icon={<DownloadOutlined />}
                                href={`${process.env.REACT_APP_API_URL}/${order.contracts[0].contract_file}`}
                                target="_blank"
                                download
                              >
                                {translate("button.contract")}
                              </Button>

                              {!order?.contracts?.[0]?.contract_file_offline &&
                                !order?.contracts?.[0]?.my_signature &&
                                !order?.contracts?.[0]?.myid_key_client &&
                                !order?.contracts?.[0]?.myid_key_director && (
                                <Button
                                    type="primary"
                                    onClick={() => OpenModalIframe(false)}
                                >
                                  {translate("button.sign")}
                                </Button>
                              )}
                              <Button
                                type="primary"
                                loading={smsLoading}
                                onClick={() => sendSms({
                                  phone: order?.client?.customerPhone,
                                  contractNumber: order?.contracts?.[0]?.contractNumber,
                                  clientId:order?.client?.id,
                                  contractDate:order?.createdAt
                                })}
                              >
                                {translate("button.send_sms")}
                              </Button>
                            </>
                          )}
                        </Space>
                        )}
                        <br />
                        <br />
                      </div>
                      <Button
                        type="default"
                        onClick={() => setOrderLogOpen(true)}
                        style={{ marginBottom: 10, width: "100%" }}
                      >
                        {translate("orderlog.button")}
                      </Button>
                      <OfflineContractUpload order={order} refetchInfo={refetchInfo} />
                      <OrderLogModal
                        orderId={order?.id}
                        open={orderLogOpen}
                        onClose={() => setOrderLogOpen(false)}
                      />
                      <div className={clsx(styles.card, styles.payment)}>
                        <div className={styles.title}>
                          <h2>{translate("payment.info")}</h2>
                          {user?.role !== "manager" && (
                            <Button
                              type="primary"
                              disabled={order?.type === "sold" && (order?.payments?.reduce((sum, p) => sum + p.amount, 0) >= order?.price)}
                              onClick={() =>
                                setSearchParams({
                                  ...searchParams,
                                  paymentModel: "true",
                                })
                              }
                            >
                              {translate("payment.add")}
                            </Button>
                          )}
                        </div>
                        <div
                          className={clsx(
                            styles["card"],

                            styles["payment-head"]
                          )}
                        >
                          {" "}
                          <p>{translate("table.column.amount")}</p>
                          <p>{translate("table.column.payment_time")}</p>
                          <p>{translate("payment.check")}</p>
                          {user?.role !== "manager" && <p>{translate("table.column.delete")}</p>}
                        </div>
                        {order.payments?.map((payment) => (
                          <div
                            className={clsx(
                              styles["card"],
                              styles["payment-card"]
                            )}
                          >
                            <p>
                              {payment.amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                              {{ uzs: "UZS", usd: "$" }[payment.currency]}{" - "}
                              {{ cash: translate("payment.cash"), installment: translate("payment.installment"), booking: translate("payment.booking"), click: translate("payment.click"), payme: translate("payment.payme") }[payment.type] || payment.type}
                            </p>
                            <p>
                              {format(payment.date ? new Date(payment.date) : payment.createdAt, "dd-MMMM yyyy", {
                                locale: uz,
                              })}
                            </p>
                            <div>
                              {payment.checkFile?.url ? (
                                <Button>
                                  <a download={'true'} target={"_blank"}
                                     href={`${process.env.REACT_APP_API_URL}/${payment.checkFile.url}`}>
                                    <EyeOutlined />
                                  </a>
                                </Button>
                              ) : "-"}
                            </div>
                            {/* {user?.role !== "manager" && ( */}
                            <div>
                              <Button
                                danger
                                onClick={() => deletePayment(payment)}
                              >
                                <MdDelete />
                              </Button>
                            </div>
                            {/* )} */}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </Modal>

      <Modal
        open={iframeModalOpen}
        onCancel={OnCancelIframe}
        footer={null}
        width="90%"
        style={{top: 20}}
        destroyOnClose
      >
        <div style={{height: "80vh"}}>
          <iframe
            id="myid_iframe"
            src={"https://docs.myid.uz/iframe"}
            allow="camera;fullscreen"
            allowFullScreen
            onLoad={handleIframeLoad}
            style={{width: "100%", height: "100%", border: "none"}}
          />
        </div>
      </Modal>
    </>
  );
}
